import { Text, makeStyles, Spinner, mergeClasses } from '@fluentui/react-components';
import { ChevronRight28Regular } from '@fluentui/react-icons';

type PrimaryButtonProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  isLoading?: boolean;
};

const useStyles = makeStyles({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    background:
      'linear-gradient(100.16deg, rgba(115, 201, 202, 0.2) 0%, rgba(57, 100, 100, 0.2) 97.72%)',
    color: '#fff',
    padding: '20px 16px 16px 24px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'transform 0.3s, box-shadow 0.3s',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '8px',
      background: 'linear-gradient(99.82deg, #50BCBD -2.04%, #3A9899 102.33%)',
      opacity: 0,
      transition: 'opacity 0.3s',
    },
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
      '::before': {
        opacity: 1,
      },
      '& .description': {
        color: '#ffffff',
      },
      '& .arrow': {
        color: '#ffffff',
      },
    },
    ':active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  buttonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
    ':hover': {
      transform: 'none',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      '::before': {
        opacity: 0,
      },
    },
    ':active': {
      transform: 'none',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    position: 'relative',
    zIndex: 1,
  },
  title: {
    color: '#ffffff',
    display: 'block',
  },
  description: {
    color: '#B6B6B6',
    display: 'block',
  },
  arrow: {
    color: '#50BCBD',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
  },
  spinner: {
    color: '#50BCBD',
  },
});

export const PrimaryButton = ({
  title,
  description,
  icon,
  onClick,
  isLoading = false,
}: PrimaryButtonProps) => {
  const styles = useStyles();
  return (
    <button
      className={mergeClasses(styles.button, isLoading && styles.buttonDisabled)}
      onClick={onClick}
      disabled={isLoading}
      aria-busy={isLoading}
    >
      <div className={styles.content}>
        {icon}
        <Text size={400} weight="semibold" role="heading" aria-level={2} className={styles.title}>
          {title}
        </Text>
        <Text
          size={200}
          weight="regular"
          className={mergeClasses(styles.description, 'description')}
        >
          {description}
        </Text>
      </div>
      <div className={mergeClasses(styles.arrow, 'arrow')}>
        {isLoading ? (
          <Spinner size="small" className={styles.spinner} aria-label="Loading" />
        ) : (
          <ChevronRight28Regular aria-hidden="true" />
        )}
      </div>
    </button>
  );
};
